import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, User, AlertCircle } from 'lucide-react';
import { login, fetchMe } from '../../lib/api';
import ASSETS from '../../assets/assets';

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    fetchMe()
      .then((user) => {
        if (user) navigate('/admin/cms', { replace: true });
      })
      .finally(() => setChecking(false));
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(username, password);
      navigate('/admin/cms', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-brand-paper flex items-center justify-center">
        <div className="animate-pulse text-brand-cinnabar font-serif tracking-widest">載入中…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-paper flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white border border-brand-gold/30 shadow-2xl rounded-sm overflow-hidden">
        <div className="bg-brand-ink text-brand-paper py-8 px-6 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-cloud opacity-10" />
          <img
            src={ASSETS.logo}
            alt={t('brand.name')}
            className="w-12 h-12 mx-auto object-contain mb-3"
          />
          <h1 className="text-2xl font-serif tracking-[0.2em]">扶陽堂後台</h1>
          <p className="text-xs text-brand-paper/50 mt-2 tracking-widest">CMS ADMIN LOGIN</p>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-5">
          {error && (
            <div className="flex items-start gap-2 text-sm text-brand-cinnabar bg-brand-cinnabar/10 px-4 py-3 rounded">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label className="block text-xs tracking-widest text-brand-ink/60 mb-1.5">帳號 USERNAME</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-ink/30" />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-brand-stone/30 border border-brand-ink/10 focus:border-brand-cinnabar focus:outline-none text-sm"
                placeholder="admin"
                autoComplete="username"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs tracking-widest text-brand-ink/60 mb-1.5">密碼 PASSWORD</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-ink/30" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-brand-stone/30 border border-brand-ink/10 focus:border-brand-cinnabar focus:outline-none text-sm"
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 bg-brand-cinnabar text-brand-paper font-sans text-sm tracking-[0.2em] hover:bg-brand-ink disabled:opacity-60 transition-colors"
          >
            {loading ? '登入中…' : '登入 LOGIN'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
